import { Link, NavLink, useLocation } from "react-router-dom";
import { useEffect, useRef, useState } from "react";
import { ShoppingBag, Menu, X, Sparkles, ChevronDown } from "lucide-react";
import { useCart } from "../lib/cart";
import { useLoyalty } from "../lib/loyalty";
import HeaderLocationPicker, { getSelectedStore } from "./HeaderLocationPicker";
import GlobalSearchBar from "./GlobalSearchBar";
import { prefetchMenu, prefetchStrainLibrary } from "../lib/api";

// Hover-prefetchers for the top-nav links. Warming the cache on
// mouseenter means by the time the user actually clicks, the data is
// already in memory (typical hover-to-click latency on desktop is
// 200-400ms which fully covers a backend response).
const PREFETCHERS = {
    Menu: prefetchMenu,
    Strains: prefetchStrainLibrary,
};

export default function Header({ brand }) {
    const [open, setOpen] = useState(false);
    const [scrolled, setScrolled] = useState(false);
    const [shopOpen, setShopOpen] = useState(false);
    const [searchExpanded, setSearchExpanded] = useState(false);
    const shopRef = useRef(null);
    const location = useLocation();
    const { itemCount, setOpen: setCartOpen } = useCart();
    const { openModal: openLoyalty, points: loyaltyPoints } = useLoyalty();
    const currentStore = getSelectedStore();

    // Auto-close mobile menu + shop dropdown whenever the URL changes.
    useEffect(() => {
        Promise.resolve().then(() => {
            setOpen(false);
            setShopOpen(false);
        });
    }, [location.pathname, location.search]);

    // When the mobile drawer is open, mark the body so the floating chat
    // widget (z-[120], bottom-right) can hide itself via CSS — otherwise it
    // covers the last few links of the menu on small screens.
    useEffect(() => {
        const cls = "zen-mobile-menu-open";
        if (open) document.body.classList.add(cls);
        else document.body.classList.remove(cls);
        return () => document.body.classList.remove(cls);
    }, [open]);

    useEffect(() => {
        const onScroll = () => setScrolled(window.scrollY > 20);
        window.addEventListener("scroll", onScroll);
        return () => window.removeEventListener("scroll", onScroll);
    }, []);

    useEffect(() => {
        const onClickOutside = (e) => {
            if (shopRef.current && !shopRef.current.contains(e.target)) {
                setShopOpen(false);
            }
        };
        document.addEventListener("mousedown", onClickOutside);
        return () => document.removeEventListener("mousedown", onClickOutside);
    }, []);

    // Shop dropdown children — commerce surfaces (Menu / Strains / Specials /
    // Brands) live under one tab. Strain library was added in iteration 29.
    const shopChildren = [
        { to: `/menu/${currentStore.slug}`, label: "Menu" },
        { to: `/strains/${currentStore.slug}`, label: "Strains" },
        { to: "/specials", label: "Specials" },
        { to: `/brands/${currentStore.slug}`, label: "Brands" },
    ];

    const links = [
        { to: "/locations", label: "Locations" },
        { to: "/loyalty", label: "ZenCircle" },
    ];

    return (
        <header
            data-testid="site-header"
            className={`sticky top-0 z-[60] transition-all duration-300 ${
                scrolled
                    ? "bg-zen-bg/85 backdrop-blur-xl border-b border-zen-ink/10"
                    : "bg-zen-bg/70 backdrop-blur-md"
            }`}
        >
            <div className="max-w-[1400px] mx-auto px-6 sm:px-10 py-4 sm:py-5 flex items-center gap-3 lg:gap-6">
                <Link
                    to="/"
                    data-testid="header-logo"
                    className="flex items-center gap-3 group shrink-0"
                >
                    {brand?.logo && (
                        <img
                            src={brand.logo}
                            alt="ZenZest"
                            className="h-9 w-auto"
                        />
                    )}
                    <span className="font-serif text-lg leading-none tracking-tight text-zen-ink hidden sm:inline">
                        weed<span className="italic text-zen-olive">.</span>near
                        me
                    </span>
                </Link>

                {/* Desktop search bar — inline in header row at xl+. When
                    focused/used it expands to take the row and we hide the
                    other navbar items so the dropdown isn't crowded. */}
                <div
                    className={`hidden xl:flex transition-all duration-200 ${
                        searchExpanded ? "flex-1 max-w-none" : "flex-1 min-w-[260px] max-w-[420px]"
                    }`}
                >
                    <GlobalSearchBar
                        variant="desktop"
                        onExpandedChange={setSearchExpanded}
                    />
                </div>

                <nav
                    className={`hidden md:flex items-center gap-5 lg:gap-6 ml-auto xl:ml-0 whitespace-nowrap ${
                        searchExpanded ? "xl:hidden" : ""
                    }`}
                >
                    {/* Shop dropdown — surfaces Menu / Strains / Specials / Brands */}
                    <div ref={shopRef} className="relative">
                        <button
                            type="button"
                            onClick={() => setShopOpen(true)}
                            onMouseEnter={() => setShopOpen(true)}
                            aria-haspopup="menu"
                            aria-expanded={shopOpen}
                            data-testid="nav-shop"
                            className="text-[11px] uppercase tracking-[0.25em] text-zen-ink-soft hover:text-zen-ink inline-flex items-center gap-1.5 transition-colors"
                        >
                            Shop
                            <ChevronDown
                                size={11}
                                strokeWidth={2}
                                className={`transition-transform ${shopOpen ? "rotate-180" : ""}`}
                            />
                        </button>
                        {shopOpen && (
                            <div
                                onMouseLeave={() => setShopOpen(false)}
                                data-testid="nav-shop-dropdown"
                                role="menu"
                                className="absolute top-full left-1/2 -translate-x-1/2 mt-3 min-w-[180px] bg-zen-bg border border-zen-ink/10 shadow-[0_18px_50px_rgba(31,36,24,0.12)] py-2 z-[70]"
                            >
                                {shopChildren.map((c) => (
                                    <NavLink
                                        key={c.to}
                                        to={c.to}
                                        onClick={() => setShopOpen(false)}
                                        onMouseEnter={() => {
                                            const fn = PREFETCHERS[c.label];
                                            if (fn && currentStore?.slug) fn(currentStore.slug);
                                        }}
                                        data-testid={`nav-shop-${c.label.toLowerCase()}`}
                                        className={({ isActive }) =>
                                            `block px-5 py-2.5 text-[11px] uppercase tracking-[0.25em] transition-colors ${
                                                isActive
                                                    ? "text-zen-olive bg-zen-olive/5"
                                                    : "text-zen-ink hover:text-zen-olive hover:bg-zen-olive/5"
                                            }`
                                        }
                                    >
                                        {c.label}
                                    </NavLink>
                                ))}
                            </div>
                        )}
                    </div>
                    {links.map((l) => (
                        <NavLink
                            key={l.to}
                            to={l.to}
                            end={l.to === "/"}
                            data-testid={`nav-${l.label.toLowerCase().replace(/\s+/g, "-")}`}
                            className={({ isActive }) =>
                                `text-[11px] uppercase tracking-[0.25em] transition-colors ${
                                    isActive
                                        ? "text-zen-olive"
                                        : "text-zen-ink-soft hover:text-zen-ink"
                                }`
                            }
                        >
                            {l.label}
                        </NavLink>
                    ))}
                </nav>

                <div
                    className={`hidden md:flex items-center gap-3 shrink-0 ${
                        searchExpanded ? "xl:hidden" : ""
                    }`}
                >
                    {/* Search icon for tablet/mid-desktop (md-lg) — the
                        inline search bar only appears at xl+ where there's
                        room. Reuses the mobile-variant for consistent UX. */}
                    <div className="xl:hidden">
                        <GlobalSearchBar variant="mobile" />
                    </div>
                    <HeaderLocationPicker />
                    <button
                        onClick={openLoyalty}
                        data-testid="header-check-points"
                        className="inline-flex items-center gap-2 border border-zen-line px-4 py-2.5 text-[11px] uppercase tracking-[0.22em] text-zen-ink hover:border-zen-olive hover:text-zen-olive transition-colors"
                    >
                        <Sparkles size={12} className="text-zen-olive" />
                        {loyaltyPoints != null ? (
                            <span>
                                <span className="font-semibold tabular-nums">
                                    {Number(loyaltyPoints).toLocaleString()}
                                </span>{" "}
                                pts
                            </span>
                        ) : (
                            <span>Check points</span>
                        )}
                    </button>
                    <button
                        onClick={() => setCartOpen(true)}
                        data-testid="header-cart-button"
                        aria-label={`Open cart (${itemCount} items)`}
                        className="relative p-3 text-zen-ink hover:text-zen-olive transition-colors"
                    >
                        <ShoppingBag size={18} />
                        {itemCount > 0 && (
                            <span
                                data-testid="header-cart-count"
                                className="absolute -top-0.5 -right-0.5 min-w-[18px] h-[18px] px-1 bg-zen-olive text-zen-bg text-[10px] flex items-center justify-center font-medium rounded-full"
                            >
                                <span data-testid="cart-count-badge">{itemCount}</span>
                            </span>
                        )}
                    </button>
                </div>

                {/* Mobile cluster — search icon + location picker + cart +
                    hamburger. Location picker is now in the header row (was
                    previously buried in the drawer); one-tap store switching
                    on mobile. */}
                <div className="md:hidden flex items-center gap-1 ml-auto">
                    <GlobalSearchBar variant="mobile" />
                    <div className="shrink-0">
                        <HeaderLocationPicker />
                    </div>
                    <button
                        onClick={() => setCartOpen(true)}
                        data-testid="header-cart-button-mobile"
                        aria-label={`Open cart (${itemCount} items)`}
                        className="relative p-3 text-zen-ink hover:text-zen-olive transition-colors min-w-[44px] min-h-[44px] flex items-center justify-center"
                    >
                        <ShoppingBag size={20} />
                        {itemCount > 0 && (
                            <span
                                data-testid="header-cart-count-mobile"
                                className="absolute top-1 right-1 min-w-[18px] h-[18px] px-1 bg-zen-olive text-zen-bg text-[10px] flex items-center justify-center font-medium rounded-full"
                            >
                                {itemCount}
                            </span>
                        )}
                    </button>
                    <button
                        data-testid="mobile-menu-toggle"
                        onClick={() => setOpen((v) => !v)}
                        className="p-3 text-zen-ink min-w-[44px] min-h-[44px] flex items-center justify-center"
                        aria-label="Toggle menu"
                        aria-expanded={open}
                    >
                        {open ? <X size={22} /> : <Menu size={22} />}
                    </button>
                </div>
            </div>

            {open && (
                <div
                    data-testid="mobile-menu"
                    className="md:hidden border-t border-zen-ink/10 bg-zen-bg px-6 py-5 flex flex-col gap-4 max-h-[calc(100vh-72px)] overflow-y-auto"
                >
                    <button
                        onClick={() => {
                            setOpen(false);
                            openLoyalty();
                        }}
                        data-testid="mobile-check-points"
                        className="inline-flex items-center justify-between gap-2 border border-zen-line px-4 py-3 text-[11px] uppercase tracking-[0.22em] text-zen-ink hover:border-zen-olive transition-colors"
                    >
                        <span className="inline-flex items-center gap-2">
                            <Sparkles size={12} className="text-zen-olive" />
                            {loyaltyPoints != null ? (
                                <span>
                                    <span className="font-semibold tabular-nums">
                                        {Number(loyaltyPoints).toLocaleString()}
                                    </span>{" "}
                                    pts
                                </span>
                            ) : (
                                <span>Check points</span>
                            )}
                        </span>
                        <ChevronDown size={11} className="-rotate-90" />
                    </button>

                    <div className="space-y-2 pt-1">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-zen-olive">
                            Shop
                        </p>
                        {shopChildren.map((c) => (
                            <NavLink
                                key={c.to}
                                to={c.to}
                                onClick={() => setOpen(false)}
                                data-testid={`mobile-nav-shop-${c.label.toLowerCase()}`}
                                className="block text-sm uppercase tracking-[0.25em] text-zen-ink hover:text-zen-olive pl-3 border-l-2 border-zen-line py-1.5"
                            >
                                {c.label}
                            </NavLink>
                        ))}
                    </div>
                    <div className="space-y-2 pt-1">
                        <p className="text-[10px] uppercase tracking-[0.3em] text-zen-olive">
                            Explore
                        </p>
                        {links.map((l) => (
                            <NavLink
                                key={l.to}
                                to={l.to}
                                end={l.to === "/"}
                                onClick={() => setOpen(false)}
                                className="block text-sm uppercase tracking-[0.25em] text-zen-ink hover:text-zen-olive pl-3 border-l-2 border-zen-line py-1.5"
                            >
                                {l.label}
                            </NavLink>
                        ))}
                    </div>
                </div>
            )}
        </header>
    );
}
